import React, { useEffect, useMemo, useRef, useState } from "react";
const FILE_BASE = process.env.REACT_APP_BACKEND_URL || "";
import {
  fmtDay, fmtDates, truncate6, sentenceCase, greetingWord, isNM, softVal,
  groupOf, GROUP_LIST, GROUP_COLORS, truncChars, clampWords, statusWords,
  cardDateVal, cmpPriority, plural,
} from "../lib/vikas";
import { Val, ReasonChip, docTitle, PanelLines, LaneIcon } from "./panels";

/* ---------------- shared helpers ---------------- */
function daysLate(due, asOf) {
  if (!due || !asOf) return 0;
  return Math.max(0, Math.round((new Date(asOf + "T00:00:00") - new Date(due + "T00:00:00")) / 86400000));
}
function splitTopic(item) {
  const s = String(item || ""); const i = s.indexOf(" - ");
  return i > 0 ? s.slice(0, i).trim() : "";
}
function splitAsk(item) {
  const s = String(item || ""); const i = s.indexOf(" - ");
  return i > 0 ? s.slice(i + 3).trim() : s;
}
const AREA_NAMES = {
  "team-open": "Team", "projects-open": "Projects", "presales-standing-jobs": "Pre-sales",
  "rspl-open-items": "RSPL", "generation-open": "Generation", "generation-closed": "Generation",
};
const AREA_ORDER = ["Generation", "Pre-sales", "RSPL", "Team", "Projects"];
function areasFrom(registers) {
  const m = {};
  for (const r of registers || []) {
    const key = String(r.register || "").replace(/\.csv$/i, "");
    const name = AREA_NAMES[key] || key;
    m[name] = m[name] || { no_date: 0, overdue: 0, live: 0 };
    m[name].no_date += r.no_date || 0; m[name].overdue += r.overdue || 0; m[name].live += r.live || 0;
  }
  return m;
}
function orderedAreas(areas) {
  const keys = Object.keys(areas).sort((a, b) => (AREA_ORDER.indexOf(a) + 1 || 99) - (AREA_ORDER.indexOf(b) + 1 || 99));
  return keys.map((k) => [k, areas[k]]);
}
function latestOf(pool, name) {
  const list = (pool || []).filter((p) => p.pool === name);
  if (!list.length) return null;
  return list.slice().sort((a, b) => (a.read_at < b.read_at ? -1 : 1))[list.length - 1];
}
function waitingMailsOf(p2, asOf) {
  return (p2.rows || []).filter(
    (r) => r.waiting === true || (r.waiting === undefined && r.queued === asOf && r.status !== "sent" && r.status !== "superseded")
  );
}
const cleanRoutineName = (n) => String(n || "")
  .replace("(IST)", "").replace(/~?\d{1,2}:\d{2}/g, "").replace(/^[A-Z]+ · /, "")
  .replace(/ · (Daily|Nightly|Weekly.*|Monthly.*|Mon[–-][A-Za-z]+)$/i, "").replace(/[·~\s]+$/, "").trim();
const firstTime = (t) => String(t || "").split("/")[0].trim();

// map human action labels (from data.actions arrays) to the backend verb
const ACTION_ALIASES = {
  "send it": "send", "ask for an edit": "edit", "chase by mail": "mail", "chase all by mail": "mail",
  "yes do it": "yes", "yes, do it": "yes", "no drop it": "drop", "no, drop it": "drop",
  "give to": "give", "give to…": "give", "run it now": "run", "pause it": "pause", "report a problem": "report",
  "raise an issue": "raise issue", "ask for data": "ask data", "ask for an update": "ask update",
  "copy link": "copy", "close it": "close", "new date": "new date", "add note": "note", "needs update": "needs update",
  "not relevant": "not relevant", "do first": "do first",
};
function normAction(label) {
  const s = String(label || "").trim().toLowerCase().replace(/[.…]+$/, "");
  return ACTION_ALIASES[s] || s;
}
// which verbs need a follow-up input (and of what kind)
const ACTION_MODE = { "new date": "date", give: "person", report: "note", edit: "note", answer: "answer", note: "note", "raise issue": "note" };

/* ---------------- card model ---------------- */
// type -> panel key for queuing
const TYPE_PANEL = {
  team: "9_team_tasks", ny: "6_needs_you", routine: "15_routines", gate: "16_gate",
  mail: "2_sent", plant: "18_plants", site: "19_project_sites", build: "5_build_plan",
  done: "17_done_today", library: "14_library", doubt: "12_doubts", stuck: "4_limbo",
  cost: "8_cost", ailane: "10_ai_lanes", keys: "20_keys", arch: "3_architecture",
  person: "9_team_tasks", mailgroup: "2_sent", usage: "8_cost",
};
// type -> block-4 action buttons [label, action, mode?]  (mode: date|person|note|answer)
const TYPE_ACTIONS = {
  team: [["Chase by mail", "mail"], ["Done", "done"], ["New date", "new date", "date"], ["Give to…", "give", "person"]],
  ny: [["Yes, do it", "yes"], ["No, drop it", "drop"]],
  routine: [["Run it now", "run"], ["Pause it", "pause"], ["Report a problem", "report", "note"]],
  gate: [["Approve", "approve"], ["Hold", "hold"], ["Reject", "reject"]],
  mail: [["Send it", "send"], ["Hold", "hold"], ["Drop", "drop"], ["Ask for an edit", "edit", "note"]],
  plant: [["Raise an issue", "raise issue", "note"], ["Ask for data", "ask data"]],
  site: [["Ask for an update", "ask update"], ["New date", "new date", "date"]],
  build: [["Do first", "do first"], ["Stop", "stop"]],
  done: [["Reopen", "reopen"]],
  doubt: [["Answer", "answer", "answer"], ["Not relevant", "not relevant"]],
  library: [["Needs update", "needs update"]],
  stuck: [], cost: [], ailane: [], keys: [], arch: [],
};

function titleOf(raw, type) {
  if (raw.title && String(raw.title).trim()) return sentenceCase(raw.title);
  if (type === "routine") return sentenceCase(cleanRoutineName(raw.name) || raw.id || "");
  if (type === "plant") return raw.plant || raw.id || "";
  if (type === "site") return [raw.job, raw.site].filter(Boolean).join(" · ") || raw.id || "";
  if (type === "team") return sentenceCase(raw.ask || splitAsk(raw.item) || raw.item || raw.id);
  if (type === "mail") return sentenceCase(raw.subject || raw.workstream || raw.id);
  return sentenceCase(raw.task || raw.name || raw.plant || raw.job || raw.id || "");
}
function topicOf(raw, type) {
  if (raw.topic) return raw.topic;
  if (type === "team") return splitTopic(raw.item) || raw.workstream || "";
  if (type === "library") return raw.kind || "";
  return raw.workstream || "";
}
function whoOf(raw) { return raw.who || raw.owner || raw.to_names || raw.from || raw.for || ""; }

// library shelf: explicit shelf field → the "Kind" fact → id prefix → "Other" (never dropped)
function shelfOf(c) {
  const r = c.raw || {};
  if (r.shelf) return String(r.shelf);
  const kf = (r.facts || []).find((f) => String(f.label || "").toLowerCase() === "kind");
  if (kf && kf.value) {
    const v = String(kf.value).toLowerCase();
    if (v.startsWith("sop")) return "SOP";
    if (v.startsWith("standard")) return "Standard";
    if (v.startsWith("template")) return "Template";
  }
  const id = String(c.id || "").toLowerCase();
  if (/^doc-sop/.test(id)) return "SOP";
  if (/^doc-standard/.test(id)) return "Standard";
  if (/^doc-template/.test(id)) return "Template";
  return "Other";
}

function mkCard(raw, type, asOf, idOverride) {
  const id = idOverride || raw.id || titleOf(raw, type);
  const dl = raw.days_late ?? daysLate(raw.due, asOf);
  const late = dl > 0 || raw.tier === "T0" || raw.status === "blocked" || (type === "site" && raw.baseline_cod && raw.baseline_cod < asOf);
  const waiting = raw.waiting === true || type === "mail" || raw.status === "held" || raw.status === "held-notified" || raw.status === "review";
  return {
    raw, type, id, panel: TYPE_PANEL[type],
    title: titleOf(raw, type),
    topic: topicOf(raw, type),
    who: whoOf(raw),
    when: raw.when || fmtDay(raw.due || raw.raised || raw.received || raw.date || raw.queued || raw.updated || ""),
    group: groupOf(raw),
    kind: raw.kind,
    what: raw.what, stands: raw.stands, move: raw.move,
    facts: Array.isArray(raw.facts) ? raw.facts : undefined,
    items: Array.isArray(raw.items) ? raw.items : undefined,
    items_total: raw.items_total,
    files: Array.isArray(raw.files) ? raw.files : undefined,
    source: raw.source,
    rank: raw.rank, why_first: raw.why_first,
    days_late: dl, tier: raw.tier, late, waiting,
    home: raw.home,
    _date: cardDateVal(raw),
  };
}

// contract rev-2: a full normalized card straight from a data row, with an explicit panel
function infoCard(raw, type, panel, id) {
  const c = mkCard(raw, type, "", id || raw.id);
  c.panel = panel || c.panel;
  return c;
}

// backend verbs that actually queue a row; anything else on a card's actions list is dropped
const VALID_VERBS = new Set([
  "move up", "move down", "yes", "drop", "later", "accept", "reject", "mail", "done",
  "new date", "decline", "give", "send", "hold", "edit", "stop", "do first", "answer",
  "not relevant", "needs update", "open", "download", "copy", "run", "pause", "report",
  "approve", "raise issue", "ask data", "ask update", "reopen", "close", "escalate", "note",
]);

// Mail actions are hidden until the mail feature works. Code is kept, not removed.
const HIDE_MAIL = true;

/* ---------------- card face ---------------- */
function Card({ card, ctx, sel, laneId, index, testid, quick, line2, numbered }) {
  const selected = sel.isSel(card.id);
  const gc = GROUP_COLORS[card.group] || "var(--accent)";
  const recvInfo = ctx.recvFor(card.panel, card.id);
  const hasIcon = quick && !recvInfo;
  const isClosed = !!(card.raw && card.raw.status === "closed");
  const pressTip = recvInfo ? `${recvInfo.by || "pressed"}${recvInfo.at ? " · " + new Date(recvInfo.at).toLocaleTimeString("en-GB", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit" }) : ""}` : null;
  const cls = ["card", isClosed ? "c-closed" : "", recvInfo ? "c-pressed" : "", card.late && !isClosed ? "c-late" : "", card.waiting && !card.late && !isClosed ? "c-wait" : "", selected ? "c-sel" : "", hasIcon ? "has-quick" : ""].join(" ").trim();
  const whenBits = [card.who ? truncChars(card.who, 14) : "", card.when].filter(Boolean).join(" · ");
  let line2main;
  if (isClosed) {
    line2main = <span className="closed-txt">Closed · {card.raw.closed ? fmtDay(card.raw.closed) : (card.when || "")}</span>;
  } else if (card.type === "routine") {
    line2main = <span>{[firstTime(card.raw.time_ist), fmtDates(card.raw.state || "")].filter(Boolean).join(" · ")}</span>;
  } else if (card.type === "gate") {
    line2main = <span>waits for {card.raw.waits_for}</span>;
  } else if (card.type === "library") {
    line2main = <span>{fmtDates(card.raw.stands || card.topic || "")}</span>;
  } else if (card.type === "plant") {
    const kwp = Number(card.raw.dc_kwp);
    const kwpTxt = Number.isFinite(kwp) && String(card.raw.dc_kwp).trim() !== "" ? `${Math.round(kwp).toLocaleString("en-IN")} kWp` : "size not set";
    const open = card.raw.open_items;
    const openN = Array.isArray(open) ? open.length : open;
    line2main = <span>{[kwpTxt, openN !== undefined && openN !== "" ? `${openN} open items` : null].filter(Boolean).join(" · ")}</span>;
  } else if (card.type === "site") {
    line2main = <span className={card.late ? "late-txt" : ""}>{[softVal(card.raw.stage), softVal(card.raw.status), card.raw.baseline_cod ? `due ${fmtDay(card.raw.baseline_cod)}` : null].filter(Boolean).join(" · ")}</span>;
  } else {
    const whyBit = card.why_first && index < 3 && !(card.late && card.days_late > 0) ? ` · ${card.why_first}` : "";
    line2main = (
      <>
        {card.topic ? <span className="topic-tag">{truncChars(card.topic, 14)}</span> : null}
        <span className={card.late ? "late-txt" : ""}>
          {whenBits}{card.late && card.days_late > 0 ? ` · ${card.days_late}d late` : ""}{whyBit}
        </span>
      </>
    );
  }
  return (
    <div className={cls} data-testid={testid} data-pressed={recvInfo ? "true" : undefined} title={pressTip || undefined}
      role="button" tabIndex={0}
      style={{ "--gc": gc }}
      onClick={() => ctx.openDrawer(<CardDrawer card={card} ctx={ctx} />)}
      onKeyDown={(e) => { if (e.key === "Enter") ctx.openDrawer(<CardDrawer card={card} ctx={ctx} />); }}
    >
      {sel.enabled && (
        <input type="checkbox" className="card-check" checked={selected} data-testid={`card-check-${card.id}`}
          onClick={(e) => { e.stopPropagation(); sel.onCheck(card, laneId, index, e.shiftKey); }}
          onChange={() => {}} aria-label="select" />
      )}
      <span className="card-l1">
        {numbered ? <span className="card-no">{numbered}</span> : null}
        <span className="card-t">{clampWords(card.title, 13)}</span>
      </span>
      <span className="card-l2">
        <span className="gdot" style={{ background: gc }} />
        <span className="l2-txt">{line2main}</span>
      </span>
      {quick && !recvInfo ? quick : null}
    </div>
  );
}

function QuickAct({ title, glyph, onClick, testid }) {
  return (
    <button className="card-quick" title={title} aria-label={title} data-testid={testid}
      onClick={(e) => { e.stopPropagation(); onClick(); }}>
      <LaneIcon name={glyph} />
    </button>
  );
}

/* a plain chip (non-card: counts, groups, etc.) */
function MiniChip({ title, sub, kind, onClick, testid, children }) {
  return (
    <div className={`card mini ${kind ? "mk-" + kind : ""}`} data-testid={testid}
      role={onClick ? "button" : undefined} tabIndex={onClick ? 0 : undefined}
      onClick={onClick} onKeyDown={onClick ? (e) => { if (e.key === "Enter") onClick(); } : undefined}
      style={onClick ? {} : { cursor: "default" }}>
      {children || (<><span className="card-l1"><span className="card-t">{title}</span></span>
        {sub ? <span className="card-l2">{sub}</span> : null}</>)}
    </div>
  );
}

/* ---------------- drawer: action controls ---------------- */
function ActionButtons({ card, ctx, buttons }) {
  const [mode, setMode] = useState(null); // {action, kind}
  const [text, setText] = useState("");
  const [who, setWho] = useState("");
  if (!ctx.canAct || !buttons.length) return null;
  const run = (action, value = "") => ctx.act(card.panel, action, card.id, value, card.group);
  const persons = ctx.owners || [];
  return (
    <div className="drawer-acts" data-testid={`acts-${card.id}`}>
      <div className="btn-row">
        {buttons.map(([label, action, m], i) => (
          <button key={action} className={`vk-btn ${i === 0 ? "vk-btn-primary" : ""}`}
            data-testid={`act-${action.replace(/\s+/g, "-")}`}
            onClick={() => {
              if (m === "date" || m === "person" || m === "note" || m === "answer") {
                setMode(mode && mode.action === action ? null : { action, kind: m });
              } else { run(action); }
            }}>{label}</button>
        ))}
      </div>
      {mode && (
        <div className="act-extra">
          {mode.kind === "person" ? (
            <select className="vk-input" value={who} onChange={(e) => setWho(e.target.value)} data-testid="drawer-person">
              <option value="">who…</option>
              {persons.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          ) : mode.kind === "date" ? (
            <input type="date" className="vk-input" value={text} onChange={(e) => setText(e.target.value)} data-testid="drawer-date" />
          ) : (
            <input className="vk-input" placeholder={mode.kind === "answer" ? "Your answer" : "One line…"}
              value={text} onChange={(e) => setText(e.target.value)}
              data-testid={mode.kind === "answer" ? "drawer-answer" : "drawer-note"} />
          )}
          <button className="vk-btn vk-btn-primary" data-testid="act-confirm"
            onClick={() => { run(mode.action, mode.kind === "person" ? who : text); setMode(null); setText(""); setWho(""); }}>
            Queue
          </button>
        </div>
      )}
    </div>
  );
}

function Mcq({ card, ctx }) {
  const [other, setOther] = useState("");
  const [showOther, setShowOther] = useState(false);
  const q = card.raw.question, opts = card.raw.options || [];
  if (!q || !opts.length) return null;
  return (
    <div className="mcq" data-testid={`mcq-${card.id}`}>
      <p className="mcq-q">{q}</p>
      <div className="mcq-opts">
        {opts.map((o, i) => (
          <button key={i} className="mcq-btn" data-testid={`mcq-opt-${i}`}
            onClick={() => ctx.act(card.panel, "answer", card.id, String(o), card.group)}>{String(o)}</button>
        ))}
        <button className="mcq-btn mcq-other" data-testid="mcq-other-btn" onClick={() => setShowOther(!showOther)}>Other…</button>
      </div>
      {showOther && (
        <div className="act-extra">
          <input className="vk-input" placeholder="Your answer" value={other} onChange={(e) => setOther(e.target.value)} data-testid="mcq-other-input" />
          <button className="vk-btn vk-btn-primary" data-testid="mcq-other-send"
            onClick={() => { ctx.act(card.panel, "answer", card.id, other, card.group); setOther(""); setShowOther(false); }}>Queue</button>
        </div>
      )}
    </div>
  );
}

function FileRow({ f }) {
  const copy = () => { try { navigator.clipboard?.writeText(f.url || ""); } catch (e) {} };
  const download = () => {
    const a = document.createElement("a"); a.href = f.url; a.download = f.name || ""; a.target = "_blank";
    document.body.appendChild(a); a.click(); a.remove();
  };
  return (
    <div className="file-row" data-testid="file-row">
      <span className="file-name">{f.name || docTitle(f.url)}{f.kind ? <span className="file-kind"> · {f.kind}</span> : null}</span>
      <span className="file-ops">
        <button className="vk-arrow" data-testid="file-open" onClick={() => f.url && window.open(f.url, "_blank", "noopener")}>Open</button>
        <button className="vk-arrow" data-testid="file-download" onClick={download}>Download</button>
        <button className="vk-arrow" data-testid="file-copy" onClick={copy}>Copy link</button>
      </span>
    </div>
  );
}

function FooterActs({ card, ctx }) {
  const [mode, setMode] = useState(null);
  const [text, setText] = useState("");
  const [esc, setEsc] = useState("To the Desk");
  const [date, setDate] = useState("");
  if (!ctx.canAct) return null;
  const run = (action, value = "") => ctx.act(card.panel, action, card.id, value, card.group);
  return (
    <div className="drawer-foot" data-testid="drawer-foot">
      <div className="btn-row">
        <button className="vk-arrow" data-testid="foot-close" onClick={() => setMode(mode === "close" ? null : "close")}>Close it</button>
        <button className="vk-arrow" data-testid="foot-escalate" onClick={() => setMode(mode === "escalate" ? null : "escalate")}>Escalate</button>
        <button className="vk-arrow" data-testid="foot-later" onClick={() => setMode(mode === "later" ? null : "later")}>Later</button>
      </div>
      {mode === "close" && (
        <div className="act-extra">
          <input className="vk-input" placeholder="Why close it?" value={text} onChange={(e) => setText(e.target.value)} data-testid="close-why" />
          <button className="vk-btn vk-btn-primary" data-testid="act-close" onClick={() => { run("close", text); setMode(null); setText(""); }}>Close</button>
        </div>
      )}
      {mode === "escalate" && (
        <div className="act-extra">
          <select className="vk-input" value={esc} onChange={(e) => setEsc(e.target.value)} data-testid="escalate-to">
            <option>To the Desk</option><option>To Eshan</option><option>To the owner</option>
          </select>
          <input className="vk-input" placeholder="Note (optional)" value={text} onChange={(e) => setText(e.target.value)} data-testid="escalate-note" />
          <button className="vk-btn vk-btn-primary" data-testid="act-escalate" onClick={() => { run("escalate", [esc, text].filter(Boolean).join(" · ")); setMode(null); setText(""); }}>Escalate</button>
        </div>
      )}
      {mode === "later" && (
        <div className="act-extra later-row">
          <button className="vk-btn" data-testid="later-tomorrow" onClick={() => { run("later", "tomorrow"); setMode(null); }}>Tomorrow</button>
          <button className="vk-btn" data-testid="later-week" onClick={() => { run("later", "next week"); setMode(null); }}>Next week</button>
          <input type="date" className="vk-input" value={date} onChange={(e) => setDate(e.target.value)} data-testid="later-date" />
          <button className="vk-btn vk-btn-primary" data-testid="act-later" disabled={!date} onClick={() => { run("later", date); setMode(null); setDate(""); }}>Set</button>
        </div>
      )}
    </div>
  );
}

/* ---------------- drawer: the 6 blocks ---------------- */
/* derive facts/items from today's rows (until contract rev 2 arrives) */
function countOf(x) { return Array.isArray(x) ? x.length : (Number(x) || 0); }
function deriveFacts(card) {
  const ok = (f) => f && f.value !== undefined && f.value !== null && f.value !== "";
  if (Array.isArray(card.facts) && card.facts.length) return card.facts.filter(ok);
  const r = card.raw || {};
  if (Array.isArray(r.facts) && r.facts.length) return r.facts.filter(ok);
  const F = (label, value) => ({ label, value });
  const pick = (pairs) => pairs.filter(([, v]) => v !== undefined && v !== null && v !== "").map(([l, v]) => F(l, typeof v === "string" ? softVal(v) : v));
  switch (card.type) {
    case "plant": return pick([["Capacity", r.dc_kwp ? `${Math.round(Number(r.dc_kwp)).toLocaleString("en-IN")} kWp` : ""], ["Data route", r.data_route], ["O&M", r.om]]);
    case "site": return pick([["Job", r.job], ["Site", r.site], ["Stage", r.stage], ["Status", r.status], ["Due", r.baseline_cod ? fmtDay(r.baseline_cod) : ""]]);
    case "routine": return pick([["Time", r.time_ist], ["Days", r.days], ["Model", r.model], ["State", fmtDates(r.state || "")], ["Last log", r.last_log ? fmtDay(r.last_log) : ""]]);
    case "team": return pick([["Owner", r.owner], ["Topic", r.topic], ["Status", statusWords(r.status)], ["Raised", r.raised ? fmtDay(r.raised) : ""], ["Due", r.due ? fmtDay(r.due) : ""], ["Channel", r.channel]]);
    case "mail": return pick([["To", r.to_names], ["Lane", r.lane], ["Status", statusWords(r.status)], ["State", fmtDates(r.state || "")], ["Waiting", r.days_waiting ? `${r.days_waiting}d` : ""], ["Queued", r.queued ? fmtDay(r.queued) : ""]]);
    case "gate": return pick([["File", r.file], ["Waits for", r.waits_for]]);
    default: return pick([["Workstream", r.workstream], ["Status", statusWords(r.status)], ["Tier", r.tier], ["Owner", r.owner], ["Raised", r.raised ? fmtDay(r.raised) : ""], ["Updated", r.updated ? fmtDay(r.updated) : ""]]);
  }
}
function deriveItems(card) {
  if (Array.isArray(card.items) && card.items.length) return card.items;
  const r = card.raw || {};
  if (Array.isArray(r.items) && r.items.length) return r.items;
  if (card.type === "plant" && Array.isArray(r.open_items))
    return r.open_items.map((i) => ({ id: i.id, title: i.title || i.id, sub: [i.owner, i.due ? `due ${fmtDay(i.due)}` : null, i.days_late ? `${i.days_late}d late` : null].filter(Boolean).join(" · "), tone: i.days_late > 0 ? "late" : null }));
  if (Array.isArray(r.rows)) return r.rows.map((i) => ({ id: i.id || i.title, title: i.title || i.task || i.id, sub: i.owner || i.status || "" }));
  return [];
}
// honest "list arrives later" line for cards that logically hold things but carry no list yet
function holdsPlaceholder(card) {
  if (card.type === "plant") return "The list arrives with the next data file";
  return null;
}

function ItemRow({ it, ctx, onOpen, checked, onCheck }) {
  const known = ctx.cardIndex && ctx.cardIndex[it.id];
  return (
    <div className={`hold-item ${it.tone === "late" ? "late-txt" : ""}`} data-testid={`hold-item-${it.id}`}>
      {ctx.canAct && <input type="checkbox" className="hold-check" checked={checked} onChange={onCheck} data-testid={`hold-check-${it.id}`} aria-label="select" />}
      <button className="hold-main" data-testid={`hold-open-${it.id}`} onClick={() => known && onOpen(known)} style={{ cursor: known ? "pointer" : "default" }}>
        <span className="hold-title">{it.title}{known ? " ›" : ""}</span>
        {it.sub ? <span className="hold-sub">{it.sub}</span> : null}
      </button>
    </div>
  );
}

export function CardDrawer({ card, ctx }) {
  const [sub, setSub] = useState(null);
  return (
    <div>
      {sub ? <button className="vk-arrow dr-back" data-testid="drawer-back" onClick={() => setSub(null)}>‹ Back</button> : null}
      <DrawerView card={sub || card} ctx={ctx} onOpenSub={setSub} />
    </div>
  );
}

function DrawerView({ card, ctx, onOpenSub }) {
  const [note, setNote] = useState("");
  const [sel, setSel] = useState({});
  const r = card.raw || {};
  const gc = GROUP_COLORS[card.group] || "var(--accent)";
  const buttons = ((Array.isArray(r.actions) && r.actions.length)
    ? r.actions.map((a) => { const v = normAction(a); return [sentenceCase(String(a)), v, ACTION_MODE[v]]; })
    : (TYPE_ACTIONS[card.type] || [])).filter(([, v]) => VALID_VERBS.has(v) && !(HIDE_MAIL && v === "mail"));
  const whatRaw = r.what || (r.detail && r.detail !== card.title ? r.detail : "") || (card.type === "team" ? r.item : "");
  const what = (whatRaw && String(whatRaw).trim() && String(whatRaw).trim() !== card.title.trim()) ? whatRaw : "";
  const stands = r.stands || "";
  const facts = deriveFacts(card);
  const items = deriveItems(card);
  const itemsTotal = card.items_total || r.items_total;
  const files = Array.isArray(r.files) && r.files.length ? r.files : (card.type === "library" && r.url ? [{ name: card.title, kind: r.kind, url: r.url }] : (card.files || []));
  const source = r.source || card.source;
  const myNotes = (ctx.queue || []).filter((q) => q.target_id === card.id && q.action === "note");
  const holds = holdsPlaceholder(card);
  const selIds = Object.keys(sel).filter((k) => sel[k]);
  const bulkItems = (action, value = "") => { selIds.forEach((id) => { const c = ctx.cardIndex && ctx.cardIndex[id]; ctx.act(c ? c.panel : card.panel, action, id, value, (c ? c.group : card.group)); }); setSel({}); };
  const groupBulk = card.kind === "mail_group" && ctx.canAct;

  return (
    <div data-testid="card-drawer">
      <div className="dr-bar" style={{ background: gc }} />
      <div className="dr-head">
        <h3 className="dr-title" data-testid="drawer-title">{card.title}</h3>
        <span className="dr-id" data-testid="drawer-id">{card.id}</span>
      </div>
      {card.group ? <div className="dr-grouptag" style={{ color: gc }}>{card.group}</div> : null}

      {what ? (<section className="dr-block" data-testid="dr-what"><div className="dr-lbl">What this is</div><p className="dr-p">{what}</p></section>) : null}

      {(stands || facts.length) ? (
        <section className="dr-block" data-testid="dr-stands">
          <div className="dr-lbl">Where it stands</div>
          {stands ? <p className="dr-p">{stands}</p> : null}
          {facts.length ? <div className="pills">{facts.map((f, i) => (
            <span className="pill" key={i} data-testid={`fact-${i}`}>{f.label}: {String(f.value)}</span>
          ))}</div> : null}
        </section>
      ) : null}

      {ctx.canAct ? (
        <section className="dr-block" data-testid="dr-move">
          <div className="dr-lbl">Your move</div>
          <Mcq card={card} ctx={ctx} />
          {groupBulk ? (
            <div className="btn-row">
              <button className="vk-btn vk-btn-primary" data-testid="act-send-all" onClick={() => (card.items || []).forEach((it) => ctx.act("2_sent", "send", it.id, "", card.group))}>Send all</button>
              <button className="vk-btn" data-testid="act-hold-all" onClick={() => (card.items || []).forEach((it) => ctx.act("2_sent", "hold", it.id, "", card.group))}>Hold all</button>
              <button className="vk-btn" data-testid="act-drop-all" onClick={() => (card.items || []).forEach((it) => ctx.act("2_sent", "drop", it.id, "", card.group))}>Drop all</button>
            </div>
          ) : (!r.question && r.move) ? <p className="dr-p ymove">{r.move}</p> : (!r.question && !buttons.length) ? <p className="dr-p nm">Nothing needed from you</p> : null}
          {!groupBulk && <ActionButtons card={card} ctx={ctx} buttons={buttons} />}
        </section>
      ) : null}

      {(items.length || holds) ? (
        <section className="dr-block" data-testid="dr-holds">
          <div className="dr-lbl">What it holds{items.length ? ` · ${plural(itemsTotal || items.length, "item", "items")}` : ""}</div>
          {items.length ? items.map((it) => (
            <ItemRow key={it.id} it={it} ctx={ctx} onOpen={onOpenSub}
              checked={!!sel[it.id]} onCheck={() => setSel((s) => ({ ...s, [it.id]: !s[it.id] }))} />
          )) : <p className="dr-p nm">{holds}</p>}
          {itemsTotal && items.length < itemsTotal ? <p className="dr-p nm">{itemsTotal - items.length} more not shown</p> : null}
          {selIds.length > 0 && (
            <div className="btn-row mt-2" data-testid="hold-bulk">
              <span className="bulk-n">{selIds.length} selected</span>
              <button className="vk-btn" data-testid="hold-bulk-close" onClick={() => bulkItems("close")}>Close</button>
              <button className="vk-btn" data-testid="hold-bulk-escalate" onClick={() => bulkItems("escalate", "To the Desk")}>Escalate</button>
              <button className="vk-btn" data-testid="hold-bulk-later" onClick={() => bulkItems("later", "next week")}>Later</button>
              <button className="vk-arrow" onClick={() => setSel({})}>Clear</button>
            </div>
          )}
        </section>
      ) : null}

      {(files.length || source) ? (
        <section className="dr-block" data-testid="dr-files">
          {files.length ? <div className="dr-lbl">Files</div> : null}
          {files.length ? files.map((f, i) => <FileRow key={i} f={f} />) : null}
          {source && source.url ? <a className="dr-source" data-testid="dr-source" href={source.url} target="_blank" rel="noopener noreferrer">Source: {source.name || "open"}</a> : (source && source.name ? <div className="dr-source" data-testid="dr-source">Source: {source.name}</div> : null)}
        </section>
      ) : null}

      {ctx.canAct ? (
        <section className="dr-block" data-testid="dr-notes">
          <div className="dr-lbl">Notes</div>
          <div className="act-extra">
            <input className="vk-input" placeholder="Add a note or feedback" value={note} onChange={(e) => setNote(e.target.value)} data-testid="note-input" />
            <button className="vk-btn vk-btn-primary" data-testid="note-save" disabled={!note.trim()} onClick={() => { ctx.act(card.panel, "note", card.id, note, card.group); setNote(""); }}>Save</button>
          </div>
          {myNotes.map((n) => (<div key={n.id} className="note-item" data-testid="note-item"><span>{n.value}</span><span className="note-meta">{n.by} · {n.at}</span></div>))}
        </section>
      ) : null}

      <FooterActs card={card} ctx={ctx} />
    </div>
  );
}

/* a simple detail drawer for info-only chips (keys, cost tools, ai lanes) */
function InfoDrawer({ title, rows, text }) {
  return (
    <div data-testid="info-drawer">
      <div className="dr-head"><h3 className="dr-title" data-testid="drawer-title">{title}</h3></div>
      {text ? <p className="dr-p">{text}</p> : null}
      {(rows || []).filter(([, v]) => v !== undefined && v !== null && v !== "").map(([k, v]) => (
        <div className="df" key={k}><span>{k}</span><Val v={typeof v === "number" ? String(v) : String(softVal(v))} empty="—" /></div>
      ))}
    </div>
  );
}

/* a header-number list drawer (I/E): shows What this is + the exact list from the KPI's ids */
function KpiDrawer({ kp, cards, ctx, active, onShowOnly, onClear }) {
  return (
    <div data-testid="kpi-drawer">
      <div className="dr-head">
        <h3 className="dr-title" data-testid="drawer-title">{kp.label}</h3>
        <span className="dr-id" data-testid="drawer-id">{kp.value}</span>
      </div>
      <section className="dr-block" data-testid="dr-what">
        <div className="dr-lbl">What this is</div>
        <p className="dr-p">{kp.sub ? sentenceCase(String(kp.sub)) + "." : `${kp.value} ${String(kp.label).toLowerCase()}.`}</p>
      </section>
      <section className="dr-block" data-testid="dr-holds">
        <div className="dr-lbl">The list · {cards.length}</div>
        {cards.length ? cards.map((c) => (
          <div key={c.id} className={`hold-item ${c.late ? "late-txt" : ""}`} data-testid={`kpi-item-${c.id}`}>
            <button className="hold-main" data-testid={`kpi-open-${c.id}`} onClick={() => ctx.openDrawer(<CardDrawer card={c} ctx={ctx} />)}>
              <span className="hold-title">{c.title} ›</span>
              <span className="hold-sub">{[c.who, c.when, c.late && c.days_late > 0 ? `${c.days_late}d late` : null].filter(Boolean).join(" · ")}</span>
            </button>
          </div>
        )) : <p className="dr-p nm">Nothing in this list.</p>}
      </section>
      <section className="dr-block" data-testid="dr-files">
        <div className="dr-lbl">Files</div>
        <div className="dr-source" data-testid="dr-source">Source: vikas-dashboard-data.json · meta.kpis.{kp.key}</div>
      </section>
      <div className="dr-block btn-row" data-testid="kpi-filter-row">
        <button className="vk-btn vk-btn-primary" data-testid="kpi-show-only" onClick={onShowOnly}>Show only these on the board</button>
        {active && <button className="vk-arrow" data-testid="kpi-clear-inline" onClick={onClear}>Clear filter</button>}
      </div>
    </div>
  );
}

/* ---------------- sparkline / ring ---------------- */
function Ring({ frac, size = 26 }) {
  const rr = 9, c = 2 * Math.PI * rr, f = Math.max(0, Math.min(1, frac || 0));
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r={rr} fill="none" stroke="var(--bar-track)" strokeWidth="2.5" />
      <circle cx="12" cy="12" r={rr} fill="none" stroke="var(--green)" strokeWidth="2.5"
        strokeDasharray={`${f * c} ${c}`} transform="rotate(-90 12 12)" strokeLinecap="round" />
    </svg>
  );
}
function SparkLine({ series, testid }) {
  if (!series.length) return null;
  const w = 132, h = 40, pad = 4, max = Math.max(1, ...series);
  const pts = series.map((v, i) => `${pad + (i * (w - pad * 2 - 22)) / Math.max(series.length - 1, 1)},${h - pad - (v / max) * (h - pad * 2)}`).join(" ");
  return (
    <span className="sparkline" data-testid={testid}>
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
        <polyline points={pts} fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinejoin="round" />
      </svg>
      <span className="sparkline-last">{series[series.length - 1]}</span>
    </span>
  );
}

/* ---------------- KPIs + top band (I) ---------------- */
export function computeKpis(data) {
  const p = data.panels || {};
  const asOf = data.meta?.as_of || "";
  const ny = (p["6_needs_you"]?.rows || []).filter((r) => r.status !== "done");
  const team = p["9_team_tasks"]?.open || [];
  const lateTeam = team.filter((r) => (r.days_late ?? 0) > 0 || (r.due && r.due < asOf)).length;
  const areas = areasFrom(p["4_limbo"]?.registers);
  const stuck = Object.values(areas).reduce((s, a) => s + a.no_date, 0);
  const sentToday = (p["2_sent"]?.rows || []).filter((r) => r.status === "sent" && r.sent === asOf).length;
  const routines = p["15_routines"]?.rows || [];
  const routinesDown = routines.filter((r) => (r.state || "").toLowerCase() !== "ran today").length;
  const waitingMails = waitingMailsOf(p["2_sent"] || {}, asOf);
  return { waiting: ny.length, late: lateTeam, stuck, sentToday, mailWaiting: waitingMails.length, routinesDown };
}

function kpiStandIns(data) {
  const p = data.panels || {};
  const asOf = data.meta?.as_of || "";
  const ny = (p["6_needs_you"]?.rows || []).filter((r) => r.status !== "done").length;
  const team = p["9_team_tasks"]?.open || [];
  const lateTeam = team.filter((r) => (r.days_late ?? 0) > 0 || (r.due && r.due < asOf)).length;
  const routines = p["15_routines"]?.rows || [];
  const didNotRun = routines.filter((r) => (r.state || "").toLowerCase() !== "ran today").length;
  const waitingMails = waitingMailsOf(p["2_sent"] || {}, asOf);
  const clientMails = waitingMails.filter((r) => (r.lane || "").toLowerCase() === "client").length;
  const sitesLate = (p["19_project_sites"]?.rows || []).filter((r) => r.status === "blocked" || (r.baseline_cod && r.baseline_cod < asOf)).length;
  const closed = (p["17_done_today"]?.rows || []).length;
  const counts = p["5_build_plan"]?.counts || {};
  const totalBuild = Object.values(counts).reduce((s, n) => s + n, 0);
  const openBuild = totalBuild - (counts.done || 0);
  return [
    { key: "needs_yes", label: "Needs your yes", value: ny + waitingMails.length, sub: "asks + mails for a yes", tone: ny + waitingMails.length > 0 ? "warn" : null, filter: { panels: ["6_needs_you", "2_sent"], mail_status: ["held", "held-notified"] } },
    { key: "late", label: "Late", value: lateTeam, sub: "oldest first below", tone: lateTeam > 0 ? "bad" : null, filter: { panels: ["9_team_tasks"], days_late_over: 0 } },
    { key: "did_not_run", label: "Did not run", value: didNotRun, sub: "routines", tone: didNotRun > 0 ? "warn" : null, filter: { panels: ["15_routines"], not_ran: true } },
    { key: "clients_at_risk", label: "Clients at risk", value: clientMails + sitesLate, sub: "held mail + late sites", tone: clientMails + sitesLate > 0 ? "warn" : null, filter: { panels: ["2_sent", "19_project_sites"], lane: "client" } },
    { key: "closed_today", label: "Closed today", value: closed, sub: `${openBuild} open`, tone: "good", filter: { panels: ["17_done_today"] } },
  ];
}

// generic matcher for a KPI's filter object (I / richer file)
function kpiCardMatch(card, filt) {
  if (!filt) return true;
  if (filt.panels && !filt.panels.includes(card.panel)) return false;
  if (filt.ids !== undefined && !filt.ids.includes(card.id)) return false;
  if (filt.not_ran && !(card.type === "routine" && (card.raw.state || "").toLowerCase() !== "ran today")) return false;
  if (filt.mail_status && card.panel === "2_sent" && !filt.mail_status.includes(card.raw.status)) return false;
  if (filt.days_late_over !== undefined && !(Number(card.days_late || 0) > filt.days_late_over)) return false;
  if (filt.lane) {
    if (card.panel === "2_sent" && (card.raw.lane || "") !== filt.lane) return false;
    if (card.panel === "19_project_sites" && !card.late) return false;
  }
  return true;
}

function Kpi({ k, active, lemon, onTap }) {
  const toneCls = lemon ? "kpi-lemon" : "";
  const numCls = !lemon && (k.tone === "bad" || k.tone === "warn") ? "kpi-warn" : k.tone === "good" ? "kpi-good" : "";
  const delta = k.delta;
  return (
    <button className={`kpi kpi-btn ${toneCls} ${active ? "kpi-active" : ""}`} data-testid={`kpi-${k.key}`} onClick={onTap}>
      <div className="kpi-num">
        <span className={numCls}>{k.value}</span>
        <span className="kpi-unit">{k.label}</span>
        {delta !== undefined && delta !== null ? (
          <span className={`kpi-delta ${delta > 0 ? "up" : "down"}`}>{delta > 0 ? "▲" : "▼"}{Math.abs(delta)}</span>
        ) : null}
      </div>
      <div className="kpi-sub">{k.sub}</div>
    </button>
  );
}

export function TopBand({ data, role, kpis, kpiFilter, setKpiFilter, onKpiOpen, onUsageOpen }) {
  const meta = data.meta || {};
  const list = kpis || (Array.isArray(meta.kpis) && meta.kpis.length ? meta.kpis : kpiStandIns(data));
  const by = {}; list.forEach((x) => { by[x.key] = x; });
  const greet = meta.greeting && String(meta.greeting).trim();
  const gp = [];
  const val = (k) => Number((by[k] || {}).value || 0);
  if (val("needs_yes") > 0) gp.push(`${val("needs_yes")} things need your yes`);
  if (val("late") > 0) gp.push(`${val("late")} ${val("late") === 1 ? "is" : "are"} late`);
  if (val("clients_at_risk") > 0) gp.push(`${val("clients_at_risk")} clients are at risk`);
  if (val("did_not_run") > 0) gp.push(`${val("did_not_run")} routine${val("did_not_run") === 1 ? "" : "s"} did not run`);
  const sentence = greet || (gp.length ? gp.join(", ") + "." : "Nothing is late, nothing is waiting.");
  const when = fmtDates(meta.built_at || "").replace(" IST", "");
  const usage = meta.usage || latestOf(data.panels?.["8_cost"]?.pool, "all");
  const uPct = usage ? (usage.percent ?? usage.pct) : null;
  const uReset = usage ? (usage.resets || usage.resets_at) : null;
  const badList = list.filter((x) => x.tone === "bad");
  const lemonKey = badList.length ? badList.reduce((a, b) => (Number(b.value) > Number(a.value) ? b : a)).key : null;
  return (
    <div className="topband" data-testid="topband">
      <PanelLines className="topband-lines" />
      <div className="topband-inner">
        <div className="tb-row">
          <div>
            <div className="tb-eyebrow" data-testid="tb-eyebrow">{when}</div>
            <div className="tb-greet" data-testid="greeting">Good {greetingWord()}, {role}. {sentence}</div>
          </div>
          <div className="kpi-strip">
            {list.map((kp) => (
              <Kpi key={kp.key} k={kp} active={kpiFilter === kp.key} lemon={kp.key === lemonKey}
                onTap={() => setKpiFilter(kpiFilter === kp.key ? null : kp.key)} />
            ))}
            {uPct != null && (
              <button className="usage-meter" data-testid="usage-meter" onClick={() => onUsageOpen && onUsageOpen()}>
                <div className="usage-txt">Usage {uPct}%{uReset ? ` · resets ${fmtDay(uReset)}` : ""}</div>
                <div className="usage-bar"><div className="usage-fill" style={{ width: `${Math.max(0, Math.min(100, uPct))}%` }} /></div>
              </button>
            )}
          </div>
        </div>
        {kpiFilter && (
          <button className="kpi-clear" data-testid="kpi-clear" onClick={() => setKpiFilter(null)}>Showing only {(by[kpiFilter] || {}).label || kpiFilter} · Clear ✕</button>
        )}
      </div>
    </div>
  );
}

/* ---------------- filter bar (J) ---------------- */
const STATUS_OPTS = [["late", "Late"], ["waiting", "Waiting"], ["running", "Running"], ["review", "Being checked"], ["done", "Done"]];
const DATE_OPTS = [["today", "Today"], ["7", "7 days"], ["30", "30 days"], ["custom", "Custom"]];

function FilterBar({ f, setF, owners, activeCount, onClear }) {
  return (
    <div className="filterbar" data-testid="filterbar">
      <div className="fb-groups">
        {["All", ...GROUP_LIST].map((g) => (
          <button key={g} className={`fb-pill ${f.group === g ? "on" : ""}`} data-testid={`filter-pill-${g}`}
            onClick={() => setF({ ...f, group: g })} style={g !== "All" ? { "--pg": GROUP_COLORS[g] } : {}}>
            {g !== "All" && <span className="gdot" style={{ background: GROUP_COLORS[g] }} />}{g}
          </button>
        ))}
      </div>
      <select className="fb-sel" value={f.person} onChange={(e) => setF({ ...f, person: e.target.value })} data-testid="filter-person">
        <option value="">Everyone</option>
        {owners.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
      <select className="fb-sel" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="filter-status">
        <option value="">Any status</option>
        {STATUS_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      <input className="fb-search" placeholder="Search title, topic, id, person" value={f.q}
        onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="filter-search" />
      <div className="fb-dates">
        {DATE_OPTS.map(([v, l]) => (
          <button key={v} className={`fb-pill ${f.date === v ? "on" : ""}`} data-testid={`filter-date-${v}`}
            onClick={() => setF({ ...f, date: v })}>{l}</button>
        ))}
        {f.date === "custom" && (
          <>
            <input type="date" className="fb-sel" value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} data-testid="filter-from" />
            <input type="date" className="fb-sel" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} data-testid="filter-to" />
          </>
        )}
      </div>
      {activeCount > 0 && (
        <button className="fb-clear" data-testid="filters-active" onClick={onClear}>Filters · {activeCount} active · Clear all</button>
      )}
    </div>
  );
}

/* ---------------- bulk bar (D) ---------------- */
function BulkBar({ sel, ctx }) {
  const [mode, setMode] = useState(null);
  const [text, setText] = useState("");
  const [date, setDate] = useState("");
  const items = sel.items();
  if (!items.length) return null;
  const allTeam = items.every((c) => c.type === "team");
  const bulk = (action, value = "") => { items.forEach((c) => ctx.act(c.panel, action, c.id, value, c.group)); sel.clear(); setMode(null); setText(""); setDate(""); };
  return (
    <div className="bulkbar" data-testid="bulkbar">
      <span className="bulk-n" data-testid="bulk-n">{items.length} selected</span>
      {!HIDE_MAIL && allTeam && <button className="vk-btn" data-testid="bulk-chase" onClick={() => bulk("mail")}>Chase all by mail</button>}
      <button className="vk-btn" data-testid="bulk-close" onClick={() => setMode(mode === "close" ? null : "close")}>Close</button>
      <button className="vk-btn" data-testid="bulk-escalate" onClick={() => bulk("escalate", "To the Desk")}>Escalate</button>
      <button className="vk-btn" data-testid="bulk-later" onClick={() => bulk("later", "next week")}>Later</button>
      <button className="vk-btn" data-testid="bulk-newdate" onClick={() => setMode(mode === "date" ? null : "date")}>New date</button>
      <button className="vk-btn" data-testid="bulk-note" onClick={() => setMode(mode === "note" ? null : "note")}>Add note</button>
      <button className="vk-arrow" data-testid="bulk-clear" onClick={() => sel.clear()}>Clear</button>
      {mode === "note" && (<span className="act-extra"><input className="vk-input" placeholder="Note…" value={text} onChange={(e) => setText(e.target.value)} data-testid="bulk-note-input" /><button className="vk-btn vk-btn-primary" onClick={() => bulk("note", text)}>Queue</button></span>)}
      {mode === "close" && (<span className="act-extra"><input className="vk-input" placeholder="Why?" value={text} onChange={(e) => setText(e.target.value)} data-testid="bulk-close-input" /><button className="vk-btn vk-btn-primary" onClick={() => bulk("close", text)}>Queue</button></span>)}
      {mode === "date" && (<span className="act-extra"><input type="date" className="vk-input" value={date} onChange={(e) => setDate(e.target.value)} data-testid="bulk-date-input" /><button className="vk-btn vk-btn-primary" disabled={!date} onClick={() => bulk("new date", date)}>Queue</button></span>)}
    </div>
  );
}

/* ---------------- lane wrapper ---------------- */
function Lane({ eyebrow, name, desc, icon, tint, hot, testid, sel, laneId, allIds, children, empty }) {
  return (
    <section className={`lane ${hot ? "lane-hot" : ""}`} data-testid={testid} style={tint ? { "--lane-tint": tint } : {}}>
      <div className="lane-side">
        <div className="lane-eyebrow">{eyebrow}</div>
        <div className="lane-name"><LaneIcon name={icon} />{name}</div>
        <div className="lane-desc">{desc}</div>
        {sel.enabled && allIds && allIds.length > 0 && (
          <button className="lane-selall" data-testid={`select-all-${laneId}`} onClick={() => sel.selectAll(laneId, allIds)}>Select all</button>
        )}
      </div>
      <div className="lane-body">
        {empty ? <span className="lane-empty" data-testid={`${testid}-empty`}>Nothing here for this filter</span> : children}
      </div>
    </section>
  );
}

function GroupLabel({ children }) { return <span className="grp-sub">{children}</span>; }

/* ---------------- board ---------------- */
const DEFAULT_FILTER = { group: "All", person: "", status: "", q: "", date: "30", from: "", to: "" };

export function MapBoard({ data, ctx, only }) {
  const p = data.panels || {};
  const asOf = ctx.asOf || "";
  const p1 = p["1_received"] || {}, p2 = p["2_sent"] || {}, p3 = p["3_architecture"] || {};
  const p4 = p["4_limbo"] || {}, p5 = p["5_build_plan"] || {}, p6 = p["6_needs_you"] || {};
  const p8 = p["8_cost"] || {}, p9 = p["9_team_tasks"] || {}, p10 = p["10_ai_lanes"] || {};
  const p12 = p["12_doubts"] || {}, p14 = p["14_library"] || {}, p15 = p["15_routines"] || {};
  const p16 = p["16_gate"] || {}, p17 = p["17_done_today"] || {}, p18 = p["18_plants"] || {};
  const p19 = p["19_project_sites"] || {}, p20 = p["20_keys"] || {};
  const p7 = p["7_ideas"] || {}, p11 = p["11_what_else"] || {}, p13 = p["13_team_pages"] || {};

  /* ---- persisted filters (J) + kpi filter (I) ---- */
  const [f, setFRaw] = useState(() => {
    try { return { ...DEFAULT_FILTER, ...JSON.parse(localStorage.getItem("vikas_filters") || "{}") }; } catch { return DEFAULT_FILTER; }
  });
  const setF = (nf) => { setFRaw(nf); localStorage.setItem("vikas_filters", JSON.stringify(nf)); };
  const [kpiFilter, setKpiFilterRaw] = useState(() => localStorage.getItem("vikas_kpi") || null);
  const setKpiFilter = (v) => { setKpiFilterRaw(v); if (v) localStorage.setItem("vikas_kpi", v); else localStorage.removeItem("vikas_kpi"); };
  const meta = data.meta || {};
  const kpis = useMemo(() => (Array.isArray(meta.kpis) && meta.kpis.length ? meta.kpis : kpiStandIns(data)), [data, meta.kpis]);

  /* ---- expand toggles ---- */
  const [more, setMore] = useState({});
  const [footerOpen, setFooterOpen] = useState(false);
  const toggleMore = (k) => setMore((m) => ({ ...m, [k]: !m[k] }));

  /* ---- selection (D) ---- */
  const [selSet, setSelSet] = useState({}); // id -> card
  const lastClick = useRef({ lane: null, index: -1, ids: [] });
  const sel = {
    enabled: ctx.canAct,
    isSel: (id) => !!selSet[id],
    items: () => Object.values(selSet),
    clear: () => setSelSet({}),
    selectAll: (laneId, cards) => setSelSet((s) => {
      const next = { ...s }; const allOn = cards.every((c) => next[c.id]);
      cards.forEach((c) => { if (allOn) delete next[c.id]; else next[c.id] = c; });
      return next;
    }),
    onCheck: (card, laneId, index, shift) => {
      setSelSet((s) => {
        const next = { ...s };
        if (shift && lastClick.current.lane === laneId && lastClick.current.index >= 0) {
          const ids = lastClick.current.ids;
          const [a, b] = [lastClick.current.index, index].sort((x, y) => x - y);
          for (let i = a; i <= b; i++) if (ids[i]) next[ids[i].id] = ids[i];
        } else {
          if (next[card.id]) delete next[card.id]; else next[card.id] = card;
        }
        return next;
      });
    },
    setLast: (laneId, index, ids) => { lastClick.current = { lane: laneId, index, ids }; },
  };
  ctx.openDrawer && (ctx._ = 0);

  /* ---- date window (J) ---- */
  const dateWindow = useMemo(() => {
    const end = asOf ? new Date(asOf + "T00:00:00") : new Date();
    const mk = (d) => d.toISOString().slice(0, 10);
    if (f.date === "today") return [mk(end), mk(end)];
    if (f.date === "7") { const s = new Date(end); s.setDate(s.getDate() - 7); return [mk(s), mk(end)]; }
    if (f.date === "30") { const s = new Date(end); s.setDate(s.getDate() - 30); return [mk(s), mk(end)]; }
    if (f.date === "custom" && (f.from || f.to)) return [f.from || "0000", f.to || "9999"];
    return null;
  }, [f.date, f.from, f.to, asOf]);

  const kpiMatch = (c) => {
    if (!kpiFilter) return true;
    const kp = (kpis || []).find((x) => x.key === kpiFilter);
    return kpiCardMatch(c, kp && kp.filter);
  };
  const statusMatch = (c) => {
    if (!f.status) return true;
    const st = (c.raw.status || "").toLowerCase();
    if (f.status === "late") return c.late;
    if (f.status === "waiting") return c.waiting;
    if (f.status === "running") return st === "running";
    if (f.status === "review") return st === "review";
    if (f.status === "done") return st === "done" || c.type === "done";
    return true;
  };
  const dateMatch = (c) => {
    if (!dateWindow) return true;
    const d = c._date;
    if (!d || !/^\d{4}-\d{2}-\d{2}/.test(d)) return true; // undated rows always pass
    return d >= dateWindow[0] && d <= dateWindow[1];
  };
  const qMatch = (c) => {
    if (!f.q.trim()) return true;
    const hay = [c.title, c.topic, c.id, c.who].join(" ").toLowerCase();
    return hay.includes(f.q.trim().toLowerCase());
  };
  const pass = (c) => (f.group === "All" || c.group === f.group)
    && (!f.person || c.who === f.person) && statusMatch(c) && dateMatch(c) && qMatch(c) && kpiMatch(c);

  // apply filters + priority sort, register lane ids for range-select
  const prep = (cards, laneId) => {
    const out = cards.filter(pass).sort(cmpPriority);
    return out;
  };

  /* ---- build card lists (contract rev-2: cards carry title/what/stands/facts/items/source) ---- */
  const ny = (p6.rows || []).filter((r) => r.status !== "done").map((r) => mkCard(r, "ny", asOf));
  const teamOpen = (p9.open || []).map((r) => mkCard(r, "team", asOf));
  const overdueTeam = teamOpen.filter((c) => c.late);
  const doToday = prep([...ny.filter((c) => c.tier === "T0"), ...overdueTeam], "doToday");
  const waitingRows = prep(ny, "waiting");
  const routines = (p15.rows || []).map((r) => mkCard(r, "routine", asOf));
  const gates = (p16.rows || []).map((g) => mkCard(g, "gate", asOf, g.file || g.id));
  const runRows = prep([...routines, ...gates], "run");
  const owners = Array.from(new Set(teamOpen.map((c) => c.who))).filter(Boolean).sort();
  const people = (p9.people || []).map((pp) => infoCard(pp, "person", "9_team_tasks", pp.id));
  const plants = (p18.rows || []).map((r) => mkCard(r, "plant", asOf, r.id || r.plant));
  const sites = (p19.rows || []).map((r) => mkCard(r, "site", asOf, r.id));
  // plants first, then sites — never interleaved (H)
  const plantSite = [...prep(plants, "plantsite"), ...prep(sites, "plantsite")];
  const buildLive = (p5.rows || []).filter((r) => r.status === "running" || r.status === "review").map((r) => mkCard(r, "build", asOf));
  const buildRows = prep(buildLive, "build");
  const buildStages = (p5.stages || []).map((st) => ({ ...st, cards: (st.rows || []).map((r) => mkCard(r, "build", asOf)) }));
  const doneAll = (p17.rows || []).map((r) => mkCard(r, "done", asOf));
  const closedHome = prep(doneAll.filter((c) => c.home === "closed"), "closed");
  const ideasCards = (p7.rows || []).map((r) => mkCard(r, "idea", asOf));
  const doneToday = prep(doneAll.filter((c) => c.raw.status !== "closed"), "done");
  const _seenClosed = new Set();
  const closedCards = [...doneAll, ...ny, ...teamOpen].filter((c) => c.raw.status === "closed" && !_seenClosed.has(c.id) && _seenClosed.add(c.id));
  const closedPrep = prep(closedCards, "closed");
  const doubts = prep((p12.rows || []).map((r) => mkCard(r, "doubt", asOf)), "doubts");
  const libRows = (p14.rows || []).map((r) => mkCard(r, "library", asOf, r.id || r.title));
  const libPass = libRows.filter(pass);
  const stuckCards = (p4.registers || []).map((rg) => infoCard(rg, "stuck", "4_limbo", rg.id));
  const costTools = (p8.outside_tools || []).map((t) => infoCard(t, "cost", "8_cost", t.id));
  const usageCard = (p8.pool || [])[0] ? infoCard((p8.pool || [])[0], "usage", "8_cost") : null;
  const aiLanes = (p10.rows || []).map((r) => infoCard(r, "ailane", "10_ai_lanes", r.id));
  const keysCard = (p20.rows || [])[0] ? infoCard((p20.rows || [])[0], "keys", "20_keys") : null;

  /* ---- mail ---- */
  const mailGroups = (p2.groups || []).map((g) => infoCard(g, "mailgroup", "2_sent", g.id));
  const sentTodayCards = (p2.rows || []).filter((r) => r.status === "sent" && r.sent === asOf).map((r) => mkCard(r, "mail", asOf));
  const sentToday = sentTodayCards.length;

  /* ---- group filter for Team / Mail / Stuck sections (tiles + recounted numbers) ---- */
  const gMatch = (raw) => f.group === "All" || groupOf(raw) === f.group;
  const teamOpenF = teamOpen.filter((c) => f.group === "All" || c.group === f.group);
  const peopleF = people.filter((pc) => gMatch(pc.raw)).map((pc) => {
    if (f.group === "All") return pc;
    const mine = teamOpenF.filter((c) => c.who === (pc.raw.owner || pc.title));
    const ids = new Set(mine.map((c) => c.id));
    const lateN = mine.filter((c) => c.days_late > 0).length;
    const items = (pc.raw.items || []).filter((it) => ids.has(it.id));
    return { ...pc, items, items_total: items.length, raw: { ...pc.raw, open: mine.length, late: lateN, items, items_total: items.length, stands: `${mine.length} open · ${lateN} late` } };
  });
  const asksF = (p9.requests || []).filter(gMatch);
  const asksOpenN = asksF.filter((a) => a.status !== "done").length;
  const showWhatsApp = f.group === "All" || f.group === "System";
  const teamEmpty = peopleF.length === 0 && asksOpenN === 0 && !showWhatsApp;
  const mailRowsFor = (g) => (p2.rows || []).filter((r) => r.lane === g.lane && String(r.status || "").startsWith(g.status || "") && gMatch(r));
  const mailGroupsF = mailGroups.filter((g) => gMatch(g.raw)).map((g) => {
    if (f.group === "All") return { card: g, n: g.raw.count ?? (g.items || []).length };
    const rows = mailRowsFor(g.raw);
    const ids = new Set(rows.map((r) => r.id));
    const items = (g.raw.items || []).filter((it) => ids.has(it.id));
    return { card: { ...g, items, items_total: rows.length, raw: { ...g.raw, count: rows.length, items, items_total: rows.length } }, n: rows.length };
  });
  const sentTodayF = sentTodayCards.filter((c) => f.group === "All" || c.group === f.group);
  const stuckF = stuckCards.filter((sc) => gMatch(sc.raw));
  const inSeries = useMemo(() => {
    const byDate = {};
    for (const list of [p1.team_requests, p1.public_requests, p1.project_updates])
      for (const d of list || []) { byDate[d.date] = (byDate[d.date] || 0) + (d.count || 0); }
    return Object.keys(byDate).sort().slice(-7).map((d) => byDate[d]);
  }, [p1]);
  const outSeries = useMemo(() => {
    const byDate = {};
    for (const r of p2.by_day_lane_status || []) { byDate[r.date] = (byDate[r.date] || 0) + (r.count || 0); }
    return Object.keys(byDate).sort().slice(-7).map((d) => byDate[d]);
  }, [p2]);
  const partial = typeof p1.source_status === "string" && p1.source_status.toUpperCase().startsWith("PARTIAL");

  /* ---- build / cost ---- */
  const counts = p5.counts || {};
  const totalBuild = Object.values(counts).reduce((s, n) => s + n, 0);
  const doneBuild = counts.done || 0;

  // card index: every id a KPI header or an item can point at, for drill-down + header lists
  ctx.owners = owners;
  const idxCards = [
    ...(p6.rows || []).map((r) => mkCard(r, "ny", asOf)),
    ...(p9.open || []).map((r) => mkCard(r, "team", asOf)),
    ...routines, ...gates,
    ...(p18.rows || []).map((r) => mkCard(r, "plant", asOf, r.id || r.plant)),
    ...(p19.rows || []).map((r) => mkCard(r, "site", asOf, r.id)),
    ...(p5.rows || []).map((r) => mkCard(r, "build", asOf)),
    ...doneAll,
    ...(p12.rows || []).map((r) => mkCard(r, "doubt", asOf)),
    ...libRows,
    ...(p2.rows || []).map((r) => mkCard(r, "mail", asOf)),
  ];
  const cardIndex = {}; idxCards.forEach((c) => { if (!cardIndex[c.id]) cardIndex[c.id] = c; });
  ctx.cardIndex = cardIndex;
  const itemize = (c) => ({ id: c.id, title: c.title, sub: [c.who, c.when, c.late && c.days_late > 0 ? `${c.days_late}d late` : null].filter(Boolean).join(" · "), tone: c.late ? "late" : null });
  const openList = (title, cards, what, group, kind, source) => ctx.openDrawer(<CardDrawer card={{ id: title, title, group: group || "System", kind: kind || "list", raw: { what: what || "", source: source || { name: "vikas-dashboard-data.json" } }, what: what || "", facts: [{ label: "Count", value: cards.length }], items: cards.map(itemize), items_total: cards.length }} ctx={ctx} />);
  const openCard = (c) => c && ctx.openDrawer(<CardDrawer card={c} ctx={ctx} />);
  const openKpi = (kp) => {
    const ids = (kp.filter && kp.filter.ids) || [];
    const cards = ids.map((id) => cardIndex[id]).filter(Boolean);
    ctx.openDrawer(<KpiDrawer kp={kp} cards={cards} ctx={ctx} active={kpiFilter === kp.key}
      onShowOnly={() => { setKpiFilter(kp.key); ctx.closeDrawer && ctx.closeDrawer(); }}
      onClear={() => { setKpiFilter(null); ctx.closeDrawer && ctx.closeDrawer(); }} />);
  };

  /* ---- filter-active count ---- */
  const activeCount = (f.group !== "All" ? 1 : 0) + (f.person ? 1 : 0) + (f.status ? 1 : 0) + (f.q.trim() ? 1 : 0) + (f.date !== "30" ? 1 : 0) + (kpiFilter ? 1 : 0);
  const clearAll = () => { setF(DEFAULT_FILTER); setKpiFilter(null); };

  // helper: render a card in a lane and register for range-select
  const renderCards = (cards, laneId, prefix, opts = {}) =>
    cards.map((c, i) => {
      sel.setLast && null;
      const quick = buildQuick(c, ctx, prefix);
      return (
        <Card key={c.type + c.id} card={c} ctx={ctx} sel={{ ...sel, onCheck: (cc, l, idx, sh) => { sel.onCheck(cc, l, idx, sh); sel.setLast(l, idx, cards); } }}
          laneId={laneId} index={i} testid={`${prefix}-${c.id}`} quick={quick}
          numbered={opts.numbered && i < 6 ? String(i + 1).padStart(2, "0") : null} />
      );
    });

  const inHome = (cards, key) => cards.filter((c) => c.home === key);
  const prepPass = (cards) => cards.filter(pass).sort(cmpPriority);
  const SEC_ICON = { do_today: "alert", waiting_for_you: "waiting", did_it_run: "gear", team: "people", mail: "mail", plants_sites: "plant", building: "build", stuck: "pause", bookshelf: "book", closed: "pause", lanes: "gear", cost: "coin", ideas: "doubt", keys: "book", suggestions: "doubt" };

  const sectionNode = (sec) => {
    const key = sec.key;
    const common = { name: sec.title, desc: sec.sub, eyebrow: "", icon: SEC_ICON[key] || "gear", testid: `section-${key}` };
    switch (key) {
      case "do_today": {
        const cs = prepPass(inHome(teamOpen, "do_today"));
        return (
          <Lane key={key} {...common} hot sel={sel} laneId="doToday" allIds={cs} empty={cs.length === 0}>
            {renderCards(cs, "doToday", "l1", { numbered: true })}
            <ShownElsewhere n={p9.shown_elsewhere} testid="shown-9_team_tasks" />
          </Lane>
        );
      }
      case "waiting_for_you": {
        const cs = prepPass(inHome(ny, "waiting_for_you"));
        const shown = more.waiting ? cs : cs.slice(0, 10);
        return (
          <Lane key={key} {...common} tint="rgba(165, 138, 243,0.07)" sel={sel} laneId="waiting" allIds={cs} empty={cs.length === 0}>
            {renderCards(shown, "waiting", "l2")}
            {!more.waiting && cs.length > 10 && <MiniChip title={`+${cs.length - 10} more`} sub="open in place" onClick={() => toggleMore("waiting")} testid="l2-more" />}
          </Lane>
        );
      }
      case "did_it_run": {
        const cs = prepPass([...routines, ...gates]);
        return (
          <Lane key={key} {...common} tint="rgba(87,180,126,0.07)" sel={sel} laneId="run" allIds={cs} empty={cs.length === 0}>
            {cs.map((c, i) => {
              const gi = cs.filter((x, j) => x.type === "gate" && j <= i).length - 1;
              return (
                <Card key={c.id} card={c} ctx={ctx} sel={{ ...sel, onCheck: (cc, l, idx, sh) => { sel.onCheck(cc, l, idx, sh); sel.setLast(l, idx, cs); } }}
                  laneId="run" index={i} testid={c.type === "gate" ? `gate-chip-${gi}` : `routine-${c.id}`} />
              );
            })}
          </Lane>
        );
      }
      case "team":
        return (
          <Lane key={key} {...common} tint="rgba(224,168,79,0.07)" sel={{ enabled: false }} laneId="team" empty={teamEmpty}>
            {peopleF.map((pc) => {
              const lateN = Number(pc.raw.late || 0);
              return (
                <div key={pc.id} className={`card mini person-chip ${lateN ? "c-late" : ""}`} data-testid={`person-${pc.raw.owner || pc.title}`} role="button" tabIndex={0}
                  style={{ "--gc": lateN ? "var(--red)" : "var(--accent)" }}
                  onClick={() => openCard(pc)} onKeyDown={(e) => { if (e.key === "Enter") openCard(pc); }}>
                  <span className="card-l1"><span className="card-t">{pc.raw.owner || pc.title} · {pc.raw.open || 0} open{lateN ? ` · ${lateN} late` : ""}</span></span>
                  <span className="card-l2"><span className="gdot" style={{ background: lateN ? "var(--red)" : "var(--accent)" }} />{fmtDates(pc.raw.stands || "tap to open")}</span>
                </div>
              );
            })}
            <MiniChip title="Team asks" sub={`${asksOpenN} open`}
              onClick={() => ctx.openDrawer(<AsksDrawer asks={asksF} ctx={ctx} persons={owners} />)} testid="team-asks-chip" />
            {showWhatsApp && p9.whatsapp && (
              <MiniChip title="WhatsApp" sub="not yet" kind="plan"
                onClick={() => ctx.openDrawer(<CardDrawer card={{ id: "whatsapp", title: "WhatsApp", group: "System", kind: "note", raw: { what: "Bringing WhatsApp messages into the desk record.", stands: fmtDates(p9.whatsapp || "Not joined yet."), source: { name: "9_team_tasks" } } }} ctx={ctx} />)} testid="whatsapp-chip" />
            )}
            <ShownElsewhere n={p13.shown_elsewhere} testid="shown-13_team_pages" />
          </Lane>
        );
      case "mail":
        return (
          <Lane key={key} {...common} tint="rgba(165, 138, 243,0.07)" sel={{ enabled: false }} laneId="mail" empty={false}>
            <div className="mail-strip">
              <span><span className="lbl">in, last 7 days {partial && <ReasonChip text={p1.source_status} testid="mail-in-partial" />}</span><br /><SparkLine series={inSeries} testid="mail-in-spark" /></span>
              <span><span className="lbl">out, last 7 days</span><br /><SparkLine series={outSeries} testid="mail-out-spark" /></span>
              <MiniChip title={`${sentTodayF.length} sent today`} sub={fmtDay(asOf)} kind="fine" testid="mail-chip-sent"
                onClick={() => openList(`Sent today · ${sentTodayF.length}`, sentTodayF, "Mail that went out today.", "Renevik", "list", { name: "2_sent" })} />
            </div>
            {mailGroupsF.length === 0 && <span className="lane-empty" data-testid="section-mail-empty">{f.group !== "All" ? "Nothing here for this filter" : "No mails waiting."}</span>}
            {mailGroupsF.map(({ card: g, n }) => (
              <MiniChip key={g.id} kind="wait" title={`${g.title} · ${n}`} sub="tap to open"
                onClick={() => openCard(g)} testid={`mail-group-${g.raw.lane || g.id}`} />
            ))}
          </Lane>
        );
      case "plants_sites": {
        const cs = plantSite.filter(pass);
        return (
          <Lane key={key} {...common} tint="rgba(87,180,126,0.07)" sel={sel} laneId="plantsite" allIds={cs} empty={cs.length === 0}>
            {cs.map((c, i) => (
              <Card key={c.type + c.id} card={c} ctx={ctx} sel={{ ...sel, onCheck: (cc, l, idx, sh) => { sel.onCheck(cc, l, idx, sh); sel.setLast(l, idx, cs); } }}
                laneId="plantsite" index={i} testid={c.type === "plant" ? `plant-${i}` : `site-${i}`} />
            ))}
          </Lane>
        );
      }
      case "building":
        return (
          <Lane key={key} {...common} tint="rgba(165, 138, 243,0.07)" sel={sel} laneId="build" empty={buildStages.length === 0}>
            {buildStages.map((st) => {
              const cs = prepPass(st.cards);
              const mk = `bstage-${st.key}`;
              const shown = more[mk] ? cs : cs.slice(0, 3);
              return (
                <React.Fragment key={st.key}>
                  <GroupLabel>{st.title} · {st.count}</GroupLabel>
                  {shown.map((c, i) => (
                    <Card key={c.id} card={c} ctx={ctx} sel={{ ...sel, onCheck: (cc, l, idx, sh) => { sel.onCheck(cc, l, idx, sh); sel.setLast(l, idx, shown); } }}
                      laneId="build" index={i} testid={`build-${st.key}-${c.id}`} />
                  ))}
                  {!more[mk] && cs.length > 3 && <MiniChip title={`+${cs.length - 3} more`} sub="open in place" onClick={() => toggleMore(mk)} testid={`build-${st.key}-more`} />}
                </React.Fragment>
              );
            })}
            <ShownElsewhere n={p5.shown_elsewhere} testid="shown-5_build_plan" />
          </Lane>
        );
      case "stuck":
        return (
          <Lane key={key} {...common} tint="rgba(224,168,79,0.06)" sel={{ enabled: false }} laneId="stuck" empty={stuckF.length === 0}>
            {stuckF.map((sc) => (
              <MiniChip key={sc.id} kind={Number(sc.raw.no_date || 0) > 0 ? "plan" : undefined}
                title={sc.title} sub={`${sc.raw.no_date ?? 0} no date · ${sc.raw.overdue ?? 0} overdue`}
                testid={`stuck-${sc.title}`} onClick={() => openCard(sc)} />
            ))}
          </Lane>
        );
      case "bookshelf": {
        const shelves = p14.shelves || [];
        const libP = libRows.filter(pass);
        const anyShelf = shelves.some((sh) => libP.some((c) => c.raw.shelf === sh.key));
        return (
          <Lane key={key} {...common} tint="rgba(87,180,126,0.06)" sel={{ enabled: false }} laneId="lib" empty={!anyShelf && !p12.count}>
            {shelves.map((sh) => {
              const cards = libP.filter((c) => c.raw.shelf === sh.key);
              if (!cards.length) return null;
              const mk = "shelf-" + sh.key;
              const shown = more[mk] ? cards : cards.slice(0, 8);
              return (
                <React.Fragment key={sh.key}>
                  <GroupLabel>{sh.title} · {sh.count}</GroupLabel>
                  {shown.map((c) => {
                    const file = c.raw.file;
                    return (
                      <div key={c.id} className={`card lib-card ${file ? "" : "c-nofile"}`} data-testid={`lib-${c.id}`} role="button" tabIndex={0}
                        onClick={() => file && ctx.openDrawer(<PdfDrawer file={file} title={c.title} />)}
                        onKeyDown={(e) => { if (e.key === "Enter" && file) ctx.openDrawer(<PdfDrawer file={file} title={c.title} />); }}>
                        <span className="card-l1"><span className="card-t">{clampWords(c.title, 13)}</span></span>
                        <span className="card-l2">{file ? fmtDates(c.raw.stands || c.topic || "open the PDF") : "No PDF on the board yet"}</span>
                      </div>
                    );
                  })}
                  {!more[mk] && cards.length > 8 && <MiniChip title={`+${cards.length - 8} more`} sub="open in place" onClick={() => toggleMore(mk)} testid={`${mk}-more`} />}
                </React.Fragment>
              );
            })}
            {p12.count != null && (
              <>
                <GroupLabel>Open-question PDF · {p12.count}</GroupLabel>
                <MiniChip title={`${p12.count} open questions`} sub={p12.file ? "open the PDF" : "no PDF yet"} kind={p12.file ? undefined : "plan"} testid="doubts-count"
                  onClick={() => p12.file && ctx.openDrawer(<PdfDrawer file={p12.file} title="Open questions" />)} />
                <ShownElsewhere n={p12.shown_elsewhere} testid="shown-12_doubts" />
              </>
            )}
          </Lane>
        );
      }
      case "closed":
        return (
          <Lane key={key} {...common} tint="rgba(130,130,130,0.05)" sel={{ enabled: false }} laneId="closed" empty={closedHome.length === 0}>
            {closedHome.map((c, i) => (
              <Card key={c.id} card={c} ctx={ctx} sel={{ enabled: false, isSel: () => false }} laneId="closed" index={i} testid={`closed-${c.id}`} />
            ))}
            <ShownElsewhere n={p17.shown_elsewhere} testid="shown-17_done_today" />
          </Lane>
        );
      case "lanes":
        return (
          <Lane key={key} {...common} tint="rgba(165, 138, 243,0.06)" sel={{ enabled: false }} laneId="lanes" empty={aiLanes.length === 0}>
            {aiLanes.map((r) => (
              <MiniChip key={r.id} title={r.title} sub={fmtDates(r.raw.stands || `lane ${r.raw.row}`)}
                onClick={() => openCard(r)} testid={`lane-chip-${r.raw.row}`} />
            ))}
          </Lane>
        );
      case "cost":
        return (
          <Lane key={key} {...common} tint="rgba(224,168,79,0.06)" sel={{ enabled: false }} laneId="cost" empty={!usageCard && costTools.length === 0}>
            {usageCard && <MiniChip title={usageCard.title} sub={fmtDates(usageCard.raw.stands || "")} onClick={() => openCard(usageCard)} testid="cost-usage-chip" />}
            {costTools.map((t, i) => { const nm = isNM(t.raw.month_to_date_inr); return (
              <MiniChip key={t.id} kind={nm ? "plan" : undefined} title={t.title} sub={nm ? "not measured" : `Rs ${t.raw.month_to_date_inr}`} onClick={() => openCard(t)} testid={`cost-tool-${i}`} />
            ); })}
          </Lane>
        );
      case "ideas": {
        const cs = prepPass(ideasCards);
        const shown = more.ideas ? cs : cs.slice(0, 10);
        return (
          <Lane key={key} {...common} tint="rgba(87,180,126,0.06)" sel={sel} laneId="ideas" allIds={cs} empty={cs.length === 0}>
            {renderCards(shown, "ideas", "idea")}
            {!more.ideas && cs.length > 10 && <MiniChip title={`+${cs.length - 10} more`} sub="open in place" onClick={() => toggleMore("ideas")} testid="ideas-more" />}
            <ShownElsewhere n={p7.shown_elsewhere} testid="shown-7_ideas" />
          </Lane>
        );
      }
      case "keys":
        return (
          <Lane key={key} {...common} tint="rgba(165, 138, 243,0.06)" sel={{ enabled: false }} laneId="keys" empty={!keysCard}>
            {keysCard && <MiniChip title={keysCard.title} sub={fmtDates(keysCard.raw.stands || plural(p20.count || 0, "key", "keys"))} onClick={() => openCard(keysCard)} testid="keys-chip" />}
          </Lane>
        );
      case "suggestions":
        return (
          <Lane key={key} {...common} tint="rgba(224,168,79,0.06)" sel={{ enabled: false }} laneId="suggestions" empty={false}>
            <MiniChip kind="plan" title="Suggestions" sub="not measured"
              onClick={() => ctx.openDrawer(<CardDrawer card={{ id: "arch-suggestions", title: "Suggestions", group: "System", kind: "note", raw: { what: fmtDates(p3.suggestions || "No suggestion list on the record yet."), stands: "Not measured yet.", source: { name: "3_architecture" } } }} ctx={ctx} />)} testid="arch-suggestions" />
            {p11.source_status && (
              <MiniChip kind="plan" title="What else" sub="no source yet"
                onClick={() => ctx.openDrawer(<CardDrawer card={{ id: "what-else", title: "What else", group: "System", kind: "note", raw: { what: fmtDates(p11.source_status), stands: "No source yet.", source: { name: "11_what_else" } } }} ctx={ctx} />)} testid="what-else" />
            )}
          </Lane>
        );
      default:
        return null;
    }
  };

  const allSecs = Array.isArray(meta.sections) ? meta.sections : [];
  const topSecs = allSecs.filter((s) => !s.footer);
  const footerSecs = allSecs.filter((s) => s.footer);

  if (only) {
    return <div data-testid="map-board-footer">{allSecs.filter((s) => only.includes(s.key)).map((sec) => sectionNode(sec))}</div>;
  }

  return (
    <div data-testid="map-board">
      <TopBand data={data} role={ctx.role} kpis={kpis} kpiFilter={kpiFilter} setKpiFilter={setKpiFilter}
        onKpiOpen={openKpi} onUsageOpen={() => openCard(usageCard)} />
      <FilterBar f={f} setF={setF} owners={owners} activeCount={activeCount} onClear={clearAll} />

      {topSecs.map((sec) => sectionNode(sec))}

      {footerSecs.length > 0 && (
        <div className="footer-fold" data-testid="footer-fold">
          <button className="footer-toggle" data-testid="footer-toggle" aria-expanded={footerOpen} onClick={() => setFooterOpen((o) => !o)}>
            {footerOpen ? "Hide" : `More — ${footerSecs.map((s) => s.title).join(" · ")}`} {footerOpen ? "▲" : "▾"}
          </button>
          {footerOpen && footerSecs.map((sec) => sectionNode(sec))}
        </div>
      )}

      <BulkBar sel={sel} ctx={ctx} />
    </div>
  );
}

/* a greyed card when no PDF is on the board yet */
function ShownElsewhere({ n, testid }) {
  if (!n) return null;
  return <div className="shown-elsewhere" data-testid={testid}>+{n} shown in another section</div>;
}

/* PDF viewer on the board + download */
export function PdfDrawer({ file, title }) {
  const src = `${FILE_BASE}${file.board_path}`;
  return (
    <div data-testid="pdf-drawer">
      <div className="dr-head"><h3 className="dr-title" data-testid="drawer-title">{title}</h3></div>
      <p className="dr-p nm">{file.name}</p>
      <div className="pdf-wrap">
        <iframe title={title} src={src} className="pdf-frame" data-testid="pdf-frame" />
      </div>
      <a className="vk-btn vk-btn-primary w-full mt-3" href={src} download={file.name} target="_blank" rel="noreferrer" data-testid="pdf-download">Download PDF</a>
    </div>
  );
}

/* quick primary action icon for the card face by lane prefix */
function buildQuick(c, ctx, prefix) {
  if (!ctx.canAct || ctx.isQueued(c.panel, c.id)) return null;
  if (c.type === "team") return HIDE_MAIL ? null : <QuickAct title="Chase by mail" glyph="mail" testid={`${prefix}-${c.id}-chase`} onClick={() => ctx.act("9_team_tasks", "mail", c.id, "", c.group)} />;
  if (c.type === "ny") return <QuickAct title="Yes, do it" glyph="check" testid={`${prefix}-${c.id}-yes`} onClick={() => ctx.act("6_needs_you", "yes", c.id, "", c.group)} />;
  return null;
}

/* team asks drawer */
function AsksDrawer({ asks, ctx, persons }) {
  const [showDone, setShowDone] = useState(false);
  const openA = asks.filter((a) => a.status !== "done");
  const doneA = asks.filter((a) => a.status === "done");
  const [who, setWho] = useState("");
  const renderAsk = (r) => (
    <div key={r.id} className="mb-4">
      <p className="dr-p" style={{ margin: "0 0 0.2rem" }}>{r.ask}</p>
      <div className="rowline-meta">{r.id} · {r.from} · {fmtDay(r.received)} · {r.status}</div>
      {r.status !== "done" && ctx.canAct && (
        <div className="btn-row mt-1">
          <button className="vk-btn vk-btn-primary" data-testid="act-accept" onClick={() => ctx.act("9_team_tasks", "accept", r.id)}>Accept</button>
          <button className="vk-btn" data-testid="act-decline" onClick={() => ctx.act("9_team_tasks", "decline", r.id)}>Decline</button>
          <select className="vk-input" value={who} onChange={(e) => setWho(e.target.value)} data-testid="drawer-person" style={{ width: "auto" }}>
            <option value="">give to…</option>{(persons || []).map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <button className="vk-btn" data-testid="act-give" onClick={() => ctx.act("9_team_tasks", "give", r.id, who)}>Give</button>
        </div>
      )}
    </div>
  );
  return (
    <div data-testid="asks-drawer">
      <div className="dr-head"><h3 className="dr-title" data-testid="drawer-title">Team asks</h3></div>
      {openA.length === 0 && <p className="nm">No open asks.</p>}
      {openA.map(renderAsk)}
      {doneA.length > 0 && <button className="showmore" data-testid="asks-show-done" onClick={() => setShowDone(!showDone)}>{showDone ? "Hide done" : `Show done · ${doneA.length}`}</button>}
      {showDone && doneA.map(renderAsk)}
      <section className="dr-block" data-testid="dr-files">
        <div className="dr-source" data-testid="dr-source">Source: team-requests.csv</div>
      </section>
    </div>
  );
}
